/**
 * QueueSoundManager - Manages all sound notifications for the queue system
 * QUEUE-007: Sound notifications for queue events
 */

export type SoundType = 'notification' | 'turn-alert' | 'warning' | 'success';

export interface SoundConfig {
  enabled: boolean;
  volume: number; // 0.0 to 1.0
  vibration?: boolean;
  soundTypes: {
    notification: boolean;
    turnAlert: boolean;
    warning: boolean;
    success: boolean;
  };
}

interface AudioCache {
  [key: string]: HTMLAudioElement;
}

export class QueueSoundManager {
  private static instance: QueueSoundManager | null = null;
  private audioCache: AudioCache = {};
  private config: SoundConfig;
  private isInitialized = false;
  private audioContext: AudioContext | null = null;
  private useSyntheticSounds = false; // Fallback to synthetic sounds if MP3s fail

  private readonly SOUND_PATHS: Record<SoundType, string> = {
    'notification': '/sounds/notification.mp3',
    'turn-alert': '/sounds/turn-alert.mp3',
    'warning': '/sounds/warning.mp3',
    'success': '/sounds/success.mp3',
  };

  // Synthetic sound configurations (frequency in Hz, duration in ms)
  private readonly SYNTHETIC_SOUNDS: Record<SoundType, { frequencies: number[]; durations: number[]; type: OscillatorType }> = {
    'notification': { frequencies: [523, 659], durations: [150, 150], type: 'sine' }, // C5, E5 - pleasant chime
    'turn-alert': { frequencies: [880, 1047, 880], durations: [100, 100, 200], type: 'sine' }, // A5, C6, A5 - attention grabbing
    'warning': { frequencies: [440, 349], durations: [200, 300], type: 'triangle' }, // A4, F4 - cautionary tone
    'success': { frequencies: [523, 659, 784], durations: [100, 100, 200], type: 'sine' }, // C5, E5, G5 - triumphant chord
  };

  private constructor() {
    // Load config from localStorage or use defaults
    const savedConfig = this.loadConfig();
    this.config = savedConfig || this.getDefaultConfig();
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): QueueSoundManager {
    if (!QueueSoundManager.instance) {
      QueueSoundManager.instance = new QueueSoundManager();
    }
    return QueueSoundManager.instance;
  }

  /**
   * Initialize the sound manager
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Create AudioContext (needed for modern browsers)
      if (typeof window !== 'undefined' && window.AudioContext) {
        this.audioContext = new AudioContext();
      }

      // Preload all sounds
      await this.preloadSounds();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize QueueSoundManager:', error);
    }
  }

  /**
   * Preload all sound files, fallback to synthetic sounds if MP3s fail
   */
  private async preloadSounds(): Promise<void> {
    let failedCount = 0;
    const totalSounds = Object.keys(this.SOUND_PATHS).length;

    const preloadPromises = Object.entries(this.SOUND_PATHS).map(
      async ([type, path]) => {
        try {
          const audio = new Audio(path);
          audio.preload = 'auto';
          audio.volume = this.config.volume;

          // Wait for the audio to be loaded with timeout
          await Promise.race([
            new Promise((resolve, reject) => {
              audio.addEventListener('canplaythrough', resolve, { once: true });
              audio.addEventListener('error', reject, { once: true });
              audio.load();
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
          ]);

          // Verify the audio has valid duration (not a placeholder)
          if (!audio.duration || audio.duration < 0.1) {
            throw new Error('Audio file appears to be invalid or placeholder');
          }

          this.audioCache[type] = audio;
        } catch (error) {
          console.warn(`Failed to preload sound ${type}, will use synthetic sound:`, error);
          failedCount++;
        }
      }
    );

    await Promise.allSettled(preloadPromises);

    // If most sounds failed, switch to synthetic sounds mode
    if (failedCount >= totalSounds / 2) {
      console.info('Using synthetic sounds (Web Audio API) as MP3 files are unavailable');
      this.useSyntheticSounds = true;
    }
  }

  /**
   * Play a sound (MP3 or synthetic fallback)
   */
  public async playSound(type: SoundType): Promise<void> {
    if (!this.config.enabled) return;

    // Check if this specific sound type is enabled
    const soundTypeKey = this.getSoundTypeKey(type);
    if (!this.config.soundTypes[soundTypeKey as keyof typeof this.config.soundTypes]) {
      return;
    }

    // Initialize if needed
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // Resume AudioContext if suspended (needed for autoplay policies)
      if (this.audioContext?.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Use synthetic sounds if MP3s are unavailable or failed
      if (this.useSyntheticSounds || !this.audioCache[type]) {
        await this.playSyntheticSound(type);
      } else {
        const audio = this.audioCache[type];
        if (audio && audio.src) {
          // Clone the audio to allow overlapping sounds
          const audioClone = audio.cloneNode() as HTMLAudioElement;
          audioClone.volume = this.config.volume;

          // Play the sound
          await audioClone.play();

          // Clean up after playing
          audioClone.addEventListener('ended', () => {
            audioClone.remove();
          });
        } else {
          // Fallback to synthetic if audio element is invalid
          await this.playSyntheticSound(type);
        }
      }

      // Trigger vibration if enabled and available
      if (this.config.vibration && 'vibrate' in navigator) {
        this.vibrate(type);
      }
    } catch (error) {
      console.warn(`Failed to play sound ${type}:`, error);
      // Try synthetic sound as last resort
      try {
        await this.playSyntheticSound(type);
      } catch {
        // Silently fail if even synthetic sounds don't work
      }
    }
  }

  /**
   * Play a synthetic sound using Web Audio API
   */
  private async playSyntheticSound(type: SoundType): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    const soundConfig = this.SYNTHETIC_SOUNDS[type];
    const { frequencies, durations, type: oscType } = soundConfig;

    let startTime = this.audioContext.currentTime;

    for (let i = 0; i < frequencies.length; i++) {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.type = oscType;
      oscillator.frequency.setValueAtTime(frequencies[i], startTime);

      // Apply volume with envelope for smoother sound
      const duration = durations[i] / 1000;
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(this.config.volume * 0.3, startTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(this.config.volume * 0.3, startTime + duration - 0.02);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);

      startTime += duration;
    }
  }

  /**
   * Trigger vibration pattern based on sound type
   */
  private vibrate(type: SoundType): void {
    if (!('vibrate' in navigator)) return;

    const patterns: Record<SoundType, number[]> = {
      'notification': [100],
      'turn-alert': [200, 100, 200],
      'warning': [300],
      'success': [50, 50, 50],
    };

    navigator.vibrate(patterns[type] || [100]);
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<SoundConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.saveConfig();
    this.updateVolume();
  }

  /**
   * Get current configuration
   */
  public getConfig(): SoundConfig {
    return { ...this.config };
  }

  /**
   * Update volume for all cached audio elements
   */
  private updateVolume(): void {
    Object.values(this.audioCache).forEach(audio => {
      audio.volume = this.config.volume;
    });
  }

  /**
   * Enable/disable sounds
   */
  public setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    this.saveConfig();
  }

  /**
   * Set volume (0.0 to 1.0)
   */
  public setVolume(volume: number): void {
    this.config.volume = Math.max(0, Math.min(1, volume));
    this.saveConfig();
    this.updateVolume();
  }

  /**
   * Enable/disable specific sound type
   */
  public setSoundTypeEnabled(type: SoundType, enabled: boolean): void {
    const key = this.getSoundTypeKey(type);
    if (key in this.config.soundTypes) {
      this.config.soundTypes[key as keyof typeof this.config.soundTypes] = enabled;
      this.saveConfig();
    }
  }

  /**
   * Convert sound type to config key
   */
  private getSoundTypeKey(type: SoundType): string {
    const mapping: Record<SoundType, string> = {
      'notification': 'notification',
      'turn-alert': 'turnAlert',
      'warning': 'warning',
      'success': 'success',
    };
    return mapping[type];
  }

  /**
   * Test a specific sound
   */
  public async testSound(type: SoundType): Promise<void> {
    const originalEnabled = this.config.enabled;
    const originalTypeEnabled = this.config.soundTypes[
      this.getSoundTypeKey(type) as keyof typeof this.config.soundTypes
    ];

    // Temporarily enable sounds for testing
    this.config.enabled = true;
    this.config.soundTypes[this.getSoundTypeKey(type) as keyof typeof this.config.soundTypes] = true;

    await this.playSound(type);

    // Restore original settings
    this.config.enabled = originalEnabled;
    this.config.soundTypes[this.getSoundTypeKey(type) as keyof typeof this.config.soundTypes] = originalTypeEnabled;
  }

  /**
   * Get default configuration
   */
  private getDefaultConfig(): SoundConfig {
    return {
      enabled: true,
      volume: 0.5,
      vibration: true,
      soundTypes: {
        notification: true,
        turnAlert: true,
        warning: true,
        success: true,
      },
    };
  }

  /**
   * Save configuration to localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem('queueSoundConfig', JSON.stringify(this.config));
    } catch (error) {
      console.warn('Failed to save sound configuration:', error);
    }
  }

  /**
   * Load configuration from localStorage
   */
  private loadConfig(): SoundConfig | null {
    try {
      const saved = localStorage.getItem('queueSoundConfig');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.warn('Failed to load sound configuration:', error);
      return null;
    }
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    // Clear audio cache
    Object.values(this.audioCache).forEach(audio => {
      audio.pause();
      audio.src = '';
    });
    this.audioCache = {};

    // Close AudioContext
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.isInitialized = false;
  }
}

// Export singleton instance
export const soundManager = QueueSoundManager.getInstance();